import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';
import { Link } from 'react-router-dom';
import { businessInfo, operatorDisclosure } from '../utils/businessInfo';

export const PrivacyPolicyPage: React.FC = () => {
    return (
        <Layout>
            <SEO
                title="Privacy Policy - Swag Shots"
                description="Swag Shots Privacy Policy - Learn how we collect, use, and protect your personal information."
                url="https://www.swagshots.com/privacy-policy"
            />

            <div className="pt-24 pb-16">
                <div className="container-custom max-w-4xl">
                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
                        <p className="text-gray-600 mb-8">Effective Date: January 1, 2025</p>

                        {/* TL;DR Summary */}
                        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8">
                            <h3 className="text-lg font-semibold text-primary-800 mb-4">TL;DR (Summary)</h3>
                            <ul className="space-y-2 text-primary-700">
                                <li>• We collect the information you give us (profile, content, and payment details where payments are available), plus device, usage, and ad/analytics data.</li>
                                <li>• We use your data to run Swag Shots, personalize content/ads (with consent where required), prevent abuse, and pay creators.</li>
                                <li>• We share data only with service providers, ad partners, and when required by law.</li>
                                <li>• You can access, correct, delete, and export your data anytime.</li>
                                <li>• Swag Shots is intended for users aged 13 and above, with extra protections for users under 18.</li>
                            </ul>
                        </div>

                        {/* Content Sections */}
                        <div className="space-y-8">
                            {/* Section 1 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    1. Controller & Contact Information
                                </h2>
                                <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">Controller:</span> {businessInfo.operatorName}, operator of Swag Shots
                                    </p>
                                    <p className="text-gray-700">
                                        {operatorDisclosure}
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">GSTIN:</span> {businessInfo.gstin}
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">Registered Address:</span> {businessInfo.registeredAddress}
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">General Contact & Support:</span>{' '}
                                        <a href={`mailto:${businessInfo.email}`} className="text-primary-600 hover:text-primary-700 underline">
                                            {businessInfo.email}
                                        </a>
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">Privacy Specific Inquiries:</span>{' '}
                                        <a href={`mailto:${businessInfo.email}`} className="text-primary-600 hover:text-primary-700 underline">
                                            {businessInfo.email}
                                        </a>
                                    </p>
                                    <p className="text-gray-700">
                                        <span className="font-semibold text-gray-900">EU/EEA Data Protection Officer:</span>{' '}
                                        <a href={`mailto:${businessInfo.email}`} className="text-primary-600 hover:text-primary-700 underline">
                                            {businessInfo.email}
                                        </a>
                                    </p>
                                </div>
                            </section>

                            {/* Section 2 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    2. Scope of this Policy
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        This Privacy Policy explains:
                                    </p>
                                    <ul className="space-y-2 ml-6">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            What data we collect
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            How we use it
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            When/why we share it
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            Your rights and choices
                                        </li>
                                    </ul>
                                    <p className="text-gray-700 leading-relaxed bg-primary-50 p-4 rounded-lg">
                                        It applies globally to all Swag Shots services (mobile apps, short video, live streaming, photo posts, messaging, stories, creator tools, and wallets where available).
                                    </p>
                                </div>
                            </section>

                            {/* Section 3 */}
                            <section className="space-y-6">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    3. Data We Collect
                                </h2>

                                <div className="space-y-6">
                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <h3 className="text-xl font-semibold text-primary-700 mb-4">A. Data You Provide</h3>
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Account:</span> name, username, email, phone, profile photo, date of birth
                                                </div>
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Content:</span> videos, livestreams, photos, captions, comments, messages
                                                </div>
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Creator data:</span> payout info (bank/UPI/PayPal), tax/KYC forms
                                                </div>
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Support:</span> help requests, reports
                                                </div>
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <h3 className="text-xl font-semibold text-primary-700 mb-4">B. Data Collected Automatically</h3>
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Usage:</span> videos watched, likes, shares, features used
                                                </div>
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Device:</span> OS, model, crash logs, IP, advertising ID
                                                </div>
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Location:</span> approximate (IP); precise only with permission
                                                </div>
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Cookies & tracking:</span> for analytics, ads, and app functionality
                                                </div>
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <h3 className="text-xl font-semibold text-primary-700 mb-4">C. Derived Data</h3>
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Recommendations:</span> interest signals for feed
                                                </div>
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">•</span>
                                                <div>
                                                    <span className="font-semibold">Aggregates:</span> non-identifying analytics reports
                                                </div>
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <h3 className="text-xl font-semibold text-primary-700 mb-4">D. From Partners</h3>
                                        <p className="text-gray-700">
                                            Linked social accounts, payment providers, identity services.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {/* Section 4 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    4. How We Use Your Data
                                </h2>
                                <div className="bg-gray-50 rounded-lg p-6">
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            Run and provide Swag Shots services
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            Personalize recommendations and ads (with consent where required)
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            Advertising & monetization (via ad networks/mediation)
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            Process payments & creator payouts
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            Enforce community guidelines, prevent fraud, ensure safety
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-primary-600 mr-3 mt-1">•</span>
                                            Analytics, product improvement, and feature testing
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Section 5 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    5. When We Share Your Data
                                </h2>
                                <div className="space-y-4">
                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <ul className="space-y-4">
                                            <li className="text-gray-700">
                                                <span className="font-semibold text-primary-700">Service Providers:</span> Cloud hosting, payment processors, customer support
                                            </li>
                                            <li className="text-gray-700">
                                                <span className="font-semibold text-primary-700">Ad Partners:</span> For targeted advertising (with consent where required)
                                            </li>
                                            <li className="text-gray-700">
                                                <span className="font-semibold text-primary-700">Legal Requirements:</span> When required by law or to protect safety
                                            </li>
                                            <li className="text-gray-700">
                                                <span className="font-semibold text-primary-700">Business Transfers:</span> In case of merger, acquisition, or sale
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Section 6 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    6. Your Rights and Choices
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed font-medium">
                                        You have the right to:
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                                            <ul className="space-y-3">
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Access:</span> Request a copy of your personal data
                                                </li>
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Correct:</span> Update inaccurate information
                                                </li>
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Delete:</span> Request deletion of your account and data
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                                            <ul className="space-y-3">
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Export:</span> Download your data in a portable format
                                                </li>
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Opt-out:</span> Marketing communications and targeted ads
                                                </li>
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Restrict:</span> Limit how we process your data
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Section 7 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    7. Data Security
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        We implement industry-standard security measures including:
                                    </p>
                                    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6">
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-green-600 mr-3 mt-1">🔒</span>
                                                Encryption of data in transit and at rest
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-blue-600 mr-3 mt-1">🔐</span>
                                                Access controls and authentication
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-purple-600 mr-3 mt-1">🛡️</span>
                                                Regular security audits and monitoring
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-orange-600 mr-3 mt-1">👨‍🏫</span>
                                                Staff training on data protection
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Section 8 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    8. Children's Privacy
                                </h2>
                                <div className="space-y-4">
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                                        <p className="text-gray-700 leading-relaxed mb-4">
                                            Swag Shots is designed for users aged 13 and above. We do not knowingly collect personal data from children under 13, and we implement special protections for users under 18:
                                        </p>
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-yellow-600 mr-3 mt-1">•</span>
                                                Limited data collection
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-yellow-600 mr-3 mt-1">•</span>
                                                Enhanced privacy controls
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-yellow-600 mr-3 mt-1">•</span>
                                                Parental consent mechanisms where required
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-yellow-600 mr-3 mt-1">•</span>
                                                Compliance with COPPA and GDPR-K
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Section 9 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    9. International Transfers
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        Your data may be transferred to countries outside your region for processing. We ensure appropriate safeguards through:
                                    </p>
                                    <div className="bg-blue-50 rounded-lg p-6">
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                                Standard Contractual Clauses
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                                Adequacy decisions
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-blue-600 mr-3 mt-1">•</span>
                                                Certified frameworks
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Section 10 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    10. Data Retention
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        We retain your data for as long as necessary to provide services and comply with legal obligations. Generally:
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                                            <ul className="space-y-3">
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Account data:</span> Until account deletion
                                                </li>
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Content:</span> Until removal by user or violation detection
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="bg-white border border-gray-200 rounded-lg p-5">
                                            <ul className="space-y-3">
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Analytics:</span> Up to 2 years in aggregated form
                                                </li>
                                                <li className="text-gray-700">
                                                    <span className="font-semibold text-primary-700">Legal compliance:</span> As required by applicable law
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Section 11 */}
                            <section className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-900 border-b-2 border-primary-600 pb-2">
                                    11. Changes to This Policy
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        We may update this policy periodically. Material changes will be communicated through:
                                    </p>
                                    <div className="bg-primary-50 rounded-lg p-6">
                                        <ul className="space-y-3">
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">📱</span>
                                                In-app notifications
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">✉️</span>
                                                Email to registered users
                                            </li>
                                            <li className="text-gray-700 flex items-start">
                                                <span className="text-primary-600 mr-3 mt-1">🌐</span>
                                                Website announcements
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* Contact Box */}
                        <div className="bg-gradient-to-br from-primary-600 to-primary-500 text-white rounded-lg p-6 mt-8 text-center">
                            <h3 className="text-xl font-semibold mb-3">Questions About Your Privacy?</h3>
                            <p className="mb-2">Contact us at:</p>
                            <p className="mb-4">
                                <strong>All Inquiries:</strong>{' '}
                                <a href={`mailto:${businessInfo.email}`} className="text-white underline hover:no-underline">
                                    {businessInfo.email}
                                </a>
                            </p>
                            <p className="text-primary-100 text-sm">
                                We handle all privacy, support, and safety questions through our main contact email.
                            </p>
                        </div>

                        <div className="text-center text-gray-500 mt-8">
                            <p>
                                <strong>Swag Shots - Create. Connect. Earn.</strong><br />
                                Operated by {businessInfo.operatorName}. GSTIN: {businessInfo.gstin}<br />
                                Your privacy is important to us.
                            </p>
                        </div>

                        {/* Back to Home */}
                        <div className="text-center mt-8">
                            <Link
                                to="/"
                                className="inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                            >
                                ← Back to Home
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
